import { getFieldValue } from "./serviceColumnValue";
// @ts-ignore - plain JS module without type declarations
import { mapObjArray } from "./viewmodelMapper.js";

/**
 * Display configuration for a service / service provider attribute row.
 *
 * The same `{ label, column, type }` shape as serviceTableColumns, extended with
 * `refFields` and `subColumn`. One config drives BOTH the GraphQL selection
 * (buildSelectionPaths) and the rendering (getServiceViewmodel), so adding a
 * field to the config is enough to fetch and show it - no code change.
 */
export interface IServiceColumn {
  label: string;
  /** Dotted path resolved against the record, e.g. "unitCost" or "tRL". */
  column: string;
  type?: TServiceColumnType;
  /**
   * For "ref"/"mref": which subfields to select and to read the display value
   * from, in order of preference. Defaults to ["label", "name"].
   *
   * Required whenever the target lacks `label`, because EMX2 fails the WHOLE
   * query on one unknown subfield: Taxons and Networks only have `name`,
   * Persons has `full_name`, Beneficiaries has neither.
   */
  refFields?: string[];
  /** For "ref"/"mref": renders "<label> (<subColumn>)", e.g. serviceCategory.name. */
  subColumn?: string;
  showCopyIcon?: boolean;
  copyValuePrefix?: string;
}

export type TServiceColumnType =
  | "string"
  | "longtext"
  | "hyperlink"
  | "email"
  | "ref"
  | "mref"
  | "bool";

export interface IBuildSelectionOptions {
  /** Prefixes every generated path, e.g. dataModel.providerRefOnService. */
  prefix?: string;
  /** Paths always selected regardless of display config (id, name, ...). */
  always?: string[];
}

const DEFAULT_REF_FIELDS = ["label", "name"];

/** Types whose value lives on a referenced record rather than on the row. */
const REF_TYPES = new Set<string>(["ref", "mref"]);

function isRefColumn(column: IServiceColumn): boolean {
  return REF_TYPES.has(column.type ?? "");
}

function refFieldsOf(column: IServiceColumn): string[] {
  return column.refFields?.length ? column.refFields : DEFAULT_REF_FIELDS;
}

function selectionPathsFor(column: IServiceColumn): string[] {
  if (!isRefColumn(column)) {
    return [column.column];
  }

  const paths = refFieldsOf(column).map(
    (refField) => `${column.column}.${refField}`
  );

  if (column.subColumn) {
    paths.push(`${column.column}.${column.subColumn}`);
  }

  return paths;
}

/**
 * Display config -> QueryEMX2 selection paths.
 *
 * Mirrors getServiceSelectionPaths() in biobanksStore: a Set seeded with the
 * always-needed identity fields, so a config that repeats them is deduped.
 * Dotted strings are valid QueryEMX2 selections - select() flattens nested
 * objects to exactly this form internally.
 */
export function buildSelectionPaths(
  columns: IServiceColumn[],
  options: IBuildSelectionOptions = {}
): string[] {
  const { prefix = "", always = [] } = options;
  const paths = new Set<string>();

  for (const path of always) {
    paths.add(path);
  }

  for (const column of columns ?? []) {
    if (!column?.column) continue;

    for (const path of selectionPathsFor(column)) {
      paths.add(prefix ? `${prefix}.${path}` : path);
    }
  }

  return [...paths];
}

/** First non-empty refField on a referenced record, e.g. label -> name. */
function refValue(item: any, refFields: string[]): string {
  if (!item) return "";

  for (const refField of refFields) {
    const value = getFieldValue(item, refField);
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }

  return "";
}

function withSubColumn(
  item: any,
  value: string,
  subColumn: string | undefined
): string {
  if (!value || !subColumn) return value;

  const context = getFieldValue(item, subColumn);
  return context ? `${value} (${context})` : value;
}

/**
 * Scalars are stringified because every view component guards on
 * `attribute.value.length` - a raw number or boolean renders nothing at all.
 */
function scalarValue(value: any): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "yes" : "no";

  return "";
}

function columnValue(record: any, column: IServiceColumn): any {
  const raw = getFieldValue(record, column.column);

  switch (column.type) {
    case "mref": {
      const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

      /** Without a subColumn, mapObjArray also resolves uri's for mref.vue. */
      if (!column.subColumn) {
        return column.refFields?.length
          ? items
              .map((item: any) => refValue(item, refFieldsOf(column)))
              .filter(Boolean)
          : mapObjArray(items);
      }

      return items
        .map((item: any) =>
          withSubColumn(item, refValue(item, refFieldsOf(column)), column.subColumn)
        )
        .filter(Boolean);
    }
    case "ref": {
      const item = Array.isArray(raw) ? raw[0] : raw;
      return withSubColumn(
        item,
        refValue(item, refFieldsOf(column)),
        column.subColumn
      );
    }
    case "bool": {
      return typeof raw === "boolean" ? scalarValue(raw) : "";
    }
    default: {
      return scalarValue(raw);
    }
  }
}

/** Config type -> the type ViewGenerator's component() switch understands. */
function viewComponentType(column: IServiceColumn): string {
  switch (column.type) {
    case "mref":
      return "mref";
    case "longtext":
    case "hyperlink":
    case "email":
      return column.type;
    /** "ref" and "bool" are already resolved to a plain string above. */
    default:
      return "string";
  }
}

/**
 * Display config -> a viewmodel for ViewGenerator.
 *
 * Uses getFieldValue for extraction rather than viewmodelMapper's
 * getObjectForValueExtraction, which cannot resolve dotted paths (it looks up
 * "service_providers.name" as a single literal key) and throws on an explicit
 * null. Empty values are left empty so the view components' own v-if hides the
 * row.
 */
export function getServiceViewmodel(
  record: any,
  columns: IServiceColumn[]
): { attributes: Record<string, any>[] } {
  const attributes: Record<string, any>[] = [];

  for (const column of columns ?? []) {
    if (!column?.column) continue;

    const value = columnValue(record, column);
    const attribute: Record<string, any> = {
      label: column.label,
      type: viewComponentType(column),
      value,
    };

    if (column.showCopyIcon && value) {
      attribute.linkValue = column.copyValuePrefix
        ? `${column.copyValuePrefix}${value}`
        : value;
    }

    attributes.push(attribute);
  }

  return { attributes };
}
